import { useMemo } from 'react';
import { ReportInternalType, ReportStatus, useGroupedReportsQuery } from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state/atoms';
import { actionStatusDisplayToEnum } from '@/utils';

export const useFetchReportGroups = (
  type: ReportInternalType,
  status: ReportStatus | 'ALL',
  searchTerm: string | undefined
) => {
  const selectedReportFilters = useAtomValue(state.selectedReportFilters);
  const structureId = useGetStructureId();

  const selectedAssemblyTemplateFilters = useMemo(() => {
    if (type !== ReportInternalType.Component) return [];

    return Object.entries(selectedReportFilters)
      .filter(([key]) => key !== 'actionStatus')
      .map(([key, value]) => ({
        id: key,
        value,
      }));
  }, [selectedReportFilters, type]);

  const reportFilters = useMemo(() => {
    return Object.entries(selectedReportFilters)
      .filter(([key]) => key === 'actionStatus')
      .map(([key, values]) => ({
        id: key,
        value: values.map((value) => actionStatusDisplayToEnum[value] ?? value),
      }));
  }, [selectedReportFilters]);

  const { data, loading, error } = useGroupedReportsQuery({
    variables: {
      input: {
        structureId,
        internalType: type,
        status: status === 'ALL' ? undefined : status,
        name: searchTerm,
        ...(type === ReportInternalType.Component && {
          selectedAssemblyTemplateFilters,
        }),
        selectedReportFilters: reportFilters,
      },
    },
    fetchPolicy: 'no-cache',
    skip: type !== ReportInternalType.Component,
  });

  const reportGroups = useMemo(() => {
    return (
      data?.groupedReports.map((group) => ({
        groupId: group?.groupId ?? '',
        groupName: group?.groupName ?? '',
        count: group?.count ?? 0,
      })) ?? []
    );
  }, [data?.groupedReports]);

  return {
    reportGroups,
    loading,
    error,
  };
};
